/**
 * @breathly/shared — validation schemas aligned to @breathly/domain.
 *
 * Per the technical constitution: validate at trust boundaries (API responses,
 * user input, storage) against the domain types before trusting them. These
 * zod schemas mirror the canonical types and produce parsed, typed values.
 *
 * Schemas are intentionally permissive where the domain is loose (e.g., Event
 * payloads are user-shaped) and strict where the domain is closed (e.g.,
 * Outcome kinds, Confidence levels).
 */
import { z } from 'zod';

/** Confidence levels — closed set (CONFIDENCE_MODEL.md). */
export const ConfidenceLevelSchema = z.enum(['very-low', 'low', 'moderate', 'high', 'very-high']);

/** ISO-8601 date string (light validation; full parsing is a presentation concern). */
export const ISODateStringSchema = z.string().min(1);

/** A non-empty string ID (branded types are applied at the type layer). */
export const IdSchema = z.string().min(1);

/** Event kind — open-ended (EVENT_PHILOSOPHY.md: loose categorization). */
export const EventKindSchema = z.string().min(1);

/** Event payload — user-shaped, intentionally permissive. */
export const EventPayloadSchema = z.record(z.string(), z.unknown());

/** An Event (EVENT_PHILOSOPHY.md: immutable, timestamped, append-only). */
export const EventSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  kind: EventKindSchema,
  occurredAt: ISODateStringSchema,
  endedAt: ISODateStringSchema.optional(),
  payload: EventPayloadSchema,
  context: z.string().optional(),
  isFuture: z.boolean().optional(),
  recordedAt: ISODateStringSchema,
});

/** Timeline page (TIMELINE_PHILOSOPHY.md: a read projection). */
export const TimelinePageSchema = z.object({
  items: z.array(EventSchema),
  nextCursor: z.string().optional(),
  hasMore: z.boolean(),
});

/** Outcome kinds — closed set (EXPERIMENT_PHILOSOPHY.md). */
export const OutcomeKindSchema = z.enum(['supported', 'unsupported', 'inconclusive', 'abandoned']);

/** A minimal health response from the API. */
export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  service: z.string(),
  version: z.string().optional(),
  timestamp: ISODateStringSchema,
});

/** Observation origin — system noticings or user-authored noticings. */
export const ObservationOriginSchema = z.enum(['system', 'user']);

/** A domain-shaped Observation (OBSERVATION_PHILOSOPHY.md). */
export const ObservationSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  statement: z.string().min(1),
  evidence: z.array(IdSchema),
  confidence: ConfidenceLevelSchema,
  confidenceBasis: z.string().optional(),
  origin: ObservationOriginSchema,
  createdAt: ISODateStringSchema,
  revisedAt: ISODateStringSchema.optional(),
  supersededBy: IdSchema.optional(),
});

/** API response shape for a single observation. */
export const ObservationResponseSchema = z.object({
  id: IdSchema,
  user_id: IdSchema,
  statement: z.string().min(1),
  evidence_event_ids: z.array(IdSchema),
  confidence: ConfidenceLevelSchema,
  confidence_basis: z.string().nullable(),
  origin: ObservationOriginSchema,
  superseded_by: IdSchema.nullable(),
  archived_at: ISODateStringSchema.nullable(),
  created_at: ISODateStringSchema,
  updated_at: ISODateStringSchema,
});

/** API response shape for a generated observation candidate. */
export const ObservationDraftResponseSchema = z.object({
  statement: z.string().min(1),
  evidence_event_ids: z.array(IdSchema),
  confidence: ConfidenceLevelSchema,
  confidence_basis: z.string().min(1),
  origin: z.literal('system'),
});

/** API response shape for a paginated observation list. */
export const ObservationListResponseSchema = z.object({
  items: z.array(ObservationResponseSchema),
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
});

export const ObservationDraftListResponseSchema = z.object({
  items: z.array(ObservationDraftResponseSchema),
});

/** Knowledge origin — system-inferred or user-authored understanding. */
export const KnowledgeOriginSchema = z.enum(['system', 'user']);
export const KnowledgeCandidateSourceSchema = z.enum(['observations', 'experiments']);

/** A domain-shaped Knowledge item (KNOWLEDGE_MODEL.md). */
export const KnowledgeSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  statement: z.string().min(1),
  evidence: z.object({
    eventIds: z.array(IdSchema),
    observationIds: z.array(IdSchema),
    experimentIds: z.array(IdSchema).optional(),
  }),
  confidence: ConfidenceLevelSchema,
  confidenceBasis: z.string().optional(),
  origin: KnowledgeOriginSchema,
  createdAt: ISODateStringSchema,
  updatedAt: ISODateStringSchema,
  staleAt: ISODateStringSchema.optional(),
  supersededBy: IdSchema.optional(),
});

/** API response shape for a single Knowledge item. */
export const KnowledgeResponseSchema = z.object({
  id: IdSchema,
  user_id: IdSchema,
  statement: z.string().min(1),
  evidence_event_ids: z.array(IdSchema),
  evidence_observation_ids: z.array(IdSchema),
  evidence_experiment_ids: z.array(IdSchema),
  confidence: ConfidenceLevelSchema,
  confidence_basis: z.string().nullable(),
  origin: KnowledgeOriginSchema,
  superseded_by: IdSchema.nullable(),
  stale_at: ISODateStringSchema.nullable(),
  archived_at: ISODateStringSchema.nullable(),
  created_at: ISODateStringSchema,
  updated_at: ISODateStringSchema,
});

/** API response shape for a generated Knowledge candidate. */
export const KnowledgeDraftResponseSchema = z.object({
  statement: z.string().min(1),
  evidence_event_ids: z.array(IdSchema),
  evidence_observation_ids: z.array(IdSchema),
  evidence_experiment_ids: z.array(IdSchema),
  confidence: ConfidenceLevelSchema,
  confidence_basis: z.string().min(1),
  origin: z.literal('system'),
  candidate_source: KnowledgeCandidateSourceSchema,
});

/** API response shape for a paginated Knowledge list. */
export const KnowledgeListResponseSchema = z.object({
  items: z.array(KnowledgeResponseSchema),
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
});

export const KnowledgeDraftListResponseSchema = z.object({
  items: z.array(KnowledgeDraftResponseSchema),
});

export const KnowledgeCandidateDismissResponseSchema = z.object({
  id: IdSchema,
  user_id: IdSchema,
  statement: z.string().min(1),
  normalized_statement: z.string().min(1),
  candidate_source: KnowledgeCandidateSourceSchema,
  dismissed_at: ISODateStringSchema,
});

/** Daily plan item status — user response to a plan item. */
export const PlanItemStatusSchema = z.enum(['proposed', 'accepted', 'declined', 'done']);

/** Origin of a persisted plan. */
export const PlanOriginSchema = z.enum(['system', 'user']);

/** A single plan item. */
export const PlanItemSchema = z.object({
  id: IdSchema,
  title: z.string().min(1),
  description: z.string().min(1),
  basis: z.string().min(1),
  status: PlanItemStatusSchema,
});

/** API response shape for a generated daily plan draft. */
export const DailyPlanDraftResponseSchema = z.object({
  plan_date: ISODateStringSchema,
  summary: z.string().min(1),
  items: z.array(PlanItemSchema),
  basis_event_ids: z.array(IdSchema),
  basis_observation_ids: z.array(IdSchema),
  basis_knowledge_ids: z.array(IdSchema),
  origin: z.literal('system'),
});

/** API response shape for a persisted daily plan. */
export const DailyPlanResponseSchema = DailyPlanDraftResponseSchema.extend({
  id: IdSchema,
  user_id: IdSchema,
  origin: PlanOriginSchema,
  archived_at: ISODateStringSchema.nullable(),
  created_at: ISODateStringSchema,
  updated_at: ISODateStringSchema,
});

/** API response shape for a paginated daily plan list. */
export const DailyPlanListResponseSchema = z.object({
  items: z.array(DailyPlanResponseSchema),
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
});

/** Experiment lifecycle status. */
export const ExperimentStatusSchema = z.enum(['proposed', 'active', 'completed', 'abandoned']);

/** Experiment outcome verdict. */
export const ExperimentOutcomeSchema = z.enum(['supported', 'unsupported', 'inconclusive', 'abandoned']);

/** Experiment origin. */
export const ExperimentOriginSchema = z.enum(['system', 'user']);

/** API response shape for an Experiment. */
export const ExperimentResponseSchema = z.object({
  id: IdSchema,
  user_id: IdSchema,
  hypothesis: z.string().min(1),
  purpose: z.string().min(1),
  protocol: z.string().min(1),
  observation_target: z.string().min(1),
  start_date: ISODateStringSchema,
  end_date: ISODateStringSchema,
  status: ExperimentStatusSchema,
  outcome: ExperimentOutcomeSchema.nullable(),
  outcome_notes: z.string().nullable(),
  confidence: ConfidenceLevelSchema,
  confidence_basis: z.string().nullable(),
  basis_event_ids: z.array(IdSchema),
  basis_observation_ids: z.array(IdSchema),
  basis_knowledge_ids: z.array(IdSchema),
  origin: ExperimentOriginSchema,
  concluded_at: ISODateStringSchema.nullable(),
  archived_at: ISODateStringSchema.nullable(),
  created_at: ISODateStringSchema,
  updated_at: ISODateStringSchema,
});

/** API response shape for a paginated Experiment list. */
export const ExperimentListResponseSchema = z.object({
  items: z.array(ExperimentResponseSchema),
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
});
